import { defineStore } from 'pinia';
// Local modules
import getters from './getters.js';
import actions from './actions.js';
import {
    isDeprecatedDomain,
    getSafeTenantURL,
    getSafeTenant,
    performDeprecatedDomainCleanup
} from '@utils/tenantValidation.js';

/**
 * Return the default authentication state kept in Pinia.
 * This now includes protection against deprecated domain initialization.
 * @returns {Object} Default state object containing token, tenant, and user data.
 */
function getDefaultState() {
    // Use safe getters that validate against deprecated domains
    const tenantURL = getSafeTenantURL();
    const tenant = getSafeTenant();

    // If we detected and cleaned deprecated domains, trigger full cleanup
    const originalTenantURL = localStorage.getItem('tenantURL');
    if (originalTenantURL && isDeprecatedDomain(originalTenantURL)) {
        console.warn(
            'Auth store detected deprecated domain during initialization, performing cleanup'
        );
        performDeprecatedDomainCleanup();

        // Return clean state
        return {
            tenant: null,
            tenantURL: null,
            token: null,
            user: null,
            codeVerifier: null,
            providerType: null
        };
    }

    // Safe to initialize with validated data
    return {
        tenant: tenant,
        tenantURL: tenantURL,
        token: JSON.parse(localStorage.getItem('token')) || null,
        user: JSON.parse(localStorage.getItem('user')) || null,
        codeVerifier: localStorage.getItem('codeVerifier') || null,
        providerType: localStorage.getItem('providerType') || null
    };
}

/**
 * Pinia store for authentication and user-session management.
 * It provides:
 *  • Reactive state (defined above)
 *  • Getters (see `getters.js`)
 *  • Actions (see `actions.js`)
 *
 * NOTE: Import this store with `import { useAuthStore } from '@stores/auth'`.
 */
export const useAuthStore = defineStore('auth', {
    state: () => ({
        ...getDefaultState()
    }),
    getters,
    actions
});

// React to storage-based logout broadcasts (multi-tab sign-out)
window.addEventListener('storage', event => {
    /* istanbul ignore next */
    if (event.key === 'logout') {
        const store = useAuthStore();
        // Avoid circular imports by acquiring the store lazily.
        store.handleLogout();
    }
});
